import {
  EventPayload,
  EventReservation,
  EventReservationHost,
  GuestUser,
} from './types';

/**
 * Reservation/guest data is transported inside the event's existing `value`
 * string field (a JSON envelope) rather than as a dedicated Avro field. This
 * keeps the Avro schema unchanged, so old and new producers/consumers share an
 * identical binary layout and there is no backward-compatibility break.
 *
 * `encodeEventFeatures` serializes the full event payload (features +
 * routing metadata like `contentChannelId`) for transport.
 */
export function encodeEventFeatures(payload: EventPayload): string {
  return JSON.stringify(payload);
}

/**
 * Parses the event `value` field back into an `EventPayload`.
 *
 * Returns `null` when the value is absent or does not describe features (e.g. a
 * non-reservation event whose `value` is a plain scalar). Only known fields are
 * copied across, so a newer producer that adds fields will not break an older
 * consumer (forward compatible), and a malformed payload never throws.
 */
export function decodeEventFeatures(
  value: string | null | undefined
): EventPayload | null {
  if (!value) {
    return null;
  }

  let parsed: unknown;
  
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }

  if (!isRecord(parsed)) {
    return null;
  }

  return {
    reservation: normalizeReservation(parsed.reservation),
    guests: normalizeGuests(parsed.guests),
    tags: normalizeTags(parsed.tags),
    category: asString(parsed.category),
    contentChannelId: asString(parsed.contentChannelId),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function normalizeHost(value: unknown): EventReservationHost {
  const host = isRecord(value) ? value : {};

  return {
    id: asString(host.id),
    name: asString(host.name),
    email: asString(host.email),
    phoneNumber: asString(host.phoneNumber),
  };
}

function normalizeReservation(value: unknown): EventReservation | null {

  if (!isRecord(value)) {
    return null;
  }

  return {
    id: asString(value.id),
    startDate: asString(value.startDate),
    endDate: asString(value.endDate),
    spaceName: asString(value.spaceName),
    host: normalizeHost(value.host),
  };
}

function normalizeGuests(value: unknown): GuestUser[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.filter(isRecord).map(guest => ({
    id: asString(guest.id),
    name: asString(guest.name),
    email: asString(guest.email),
    company: asString(guest.company),
  }));
}

function normalizeTags(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const tags = value.filter((tag): tag is string => typeof tag === 'string');

  return tags.length > 0 ? tags : null;
}